/** Typed API surface for the custom-field engine and service catalogue. */
import { apiFetch, apiSend } from '../../lib/api';
import type { FieldDefinition, FieldType, ResolvedField, Condition } from '@itsm/shared';

export interface CatalogueCategory {
  id: string;
  parentId: string | null;
  name: string;
  sortOrder: number;
}
export interface CatalogueItem {
  id: string;
  categoryId: string | null; // primary (first pick) — kept for old callers
  categoryIds: string[]; // the real membership: an item can sit in many folders
  /** Step 47 — the approval this item always needs: none | manager | group |
   *  users. Closed set; a conditional approval is an automation. */
  approvalMode?: string | null;
  approvalGroupId?: string | null;
  /** Step 101 — the CAB board, when `approvalMode` is 'board'. */
  approvalBoardId?: string | null;
  approvalUserIds?: string[] | null;
  name: string;
  description: string | null;
  icon: string | null;
  formLayoutId: string | null;
  defaultGroupId: string | null;
  /** Step 72 — offer the "for someone else" toggle on this item. */
  allowOnBehalf?: number;
  /** Step 73 — may this request carry files, and must it. */
  allowAttachments?: number;
  requireAttachment?: number;
  iconAttachmentId?: string | null;
  defaultPriority: 'low' | 'medium' | 'high' | 'urgent' | null;
  subjectTemplate: string | null;
  descriptionHtml: string | null;
  cost: string | null;
  costVisible: number;
  deliveryTime: string | null;
  deliveryVisible: number;
  isActive: number;
  isPopular: number;
  sortOrder: number;
  /** Step 69 — 'all' or 'groups' per audience, with the list that goes with
   *  it. Optional because an item created before the axes existed reads as
   *  'all', which is what the column defaults to. */
  agentVisibility?: 'all' | 'groups';
  requesterVisibility?: 'all' | 'groups';
  agentGroupIds?: string[];
  requesterGroupIds?: string[];
}

/** Step 69 — a set of requesters, by hand or by rule. */
export interface RequesterGroup {
  id: string;
  name: string;
  rule: Condition | null;
  memberIds: string[];
}
export interface FormLayoutSummary {
  id: string;
  name: string;
  appliesTo: string | null;
  isActive: number;
  sections: LayoutSectionInput[];
}
export interface LayoutSectionFieldInput {
  fieldId: string;
  required: boolean;
  audience: 'both' | 'agent-only';
  /** Step 66 — absent means the historic default (visible ⇒ editable). */
  requesterCanEdit?: boolean;
  filterable: boolean;
}
export interface LayoutSectionInput {
  title: string;
  showWhen?: Condition;
  fields: LayoutSectionFieldInput[];
}

export const fieldsApi = {
  /** Everywhere a field is referenced — layouts, the items riding them, and
   *  how many stored answers exist (Step 67's usage inspector). */
  usage: (id: string) =>
    apiFetch<{
      layouts: { id: string; name: string; isActive: number; items: { id: string; name: string }[] }[];
      itemCount: number;
      valueCount: number;
      hasBeenUsed: boolean;
      canDelete: boolean;
    }>(`/api/admin/field-definitions/${id}/usage`),
  remove: (id: string) => apiSend(`/api/admin/field-definitions/${id}`, 'DELETE'),
  /**
   * Step 94 — scope is now explicit at every call site.
   *
   *   'ticket'  fields on the ticket form (the Ticket fields admin page)
   *   'shared'  reusable across catalogue items — the item editor's picker
   *   'item'    one catalogue item's own questions
   *   'all'     everything, for resolving a field by id
   *
   * Defaulting silently is what let 64 one-off service-request questions
   * surface as though they were library content, so there is no default here.
   */
  list: (scope: 'ticket' | 'shared' | 'item' | 'all' = 'ticket') =>
    apiFetch<{ entries: FieldDefinition[] }>(`/api/admin/field-definitions?scope=${scope}`),
  create: (body: {
    key: string;
    label: string;
    helpText?: string;
    type: FieldType;
    config?: object;
    /** Step 94's three scopes. Passed explicitly by the item editor so the
     *  intent is in the call, not inherited from a server default. */
    scope?: 'item' | 'shared' | 'ticket';
  }) => apiSend<{ id: string }>('/api/admin/field-definitions', 'POST', body),
  update: (
    id: string,
    body: { label?: string; helpText?: string; type?: FieldType; config?: object; isActive?: boolean },
  ) => apiSend(`/api/admin/field-definitions/${id}`, 'PATCH', body),
};

export const formLayoutsApi = {
  list: () => apiFetch<{ entries: FormLayoutSummary[] }>('/api/admin/form-layouts'),
  resolved: (id: string, surface?: 'agent' | 'requester') =>
    apiFetch<{ fields: ResolvedField[] }>(
      `/api/admin/form-layouts/${id}/resolved${surface ? `?surface=${surface}` : ''}`,
    ),
  create: (body: { name: string; appliesTo?: string; sections: LayoutSectionInput[]; isActive?: boolean }) =>
    apiSend<{ id: string }>('/api/admin/form-layouts', 'POST', body),
  update: (
    id: string,
    body: Partial<{ name: string; appliesTo: string; sections: LayoutSectionInput[]; isActive: boolean }>,
  ) => apiSend(`/api/admin/form-layouts/${id}`, 'PATCH', body),
};

export const catalogueApi = {
  categories: () => apiFetch<{ entries: CatalogueCategory[] }>('/api/catalogue/categories'),
  createCategory: (name: string, parentId?: string) =>
    apiSend<{ id: string }>('/api/catalogue/categories', 'POST', {
      name,
      ...(parentId ? { parentId } : {}),
    }),
  updateCategory: (id: string, body: { name?: string; parentId?: string | null }) =>
    apiSend(`/api/catalogue/categories/${id}`, 'PATCH', body),
  deleteCategory: (id: string) => apiSend(`/api/catalogue/categories/${id}`, 'DELETE'),
  cloneItem: (id: string, name?: string) =>
    apiSend<{ id: string }>(`/api/catalogue/items/${id}/clone`, 'POST', name ? { name } : {}),
  items: (activeOnly?: boolean) =>
    apiFetch<{ entries: CatalogueItem[]; favourites: string[]; popular: string[] }>(
      `/api/catalogue/items${activeOnly ? '?active=1' : ''}`,
    ),
  /** Step 68 bulk ops. `delete` is PARTIAL by design — items with tickets
   *  raised from them come back in `blocked` rather than failing the batch. */
  bulk: (body: {
    ids: string[];
    action: 'move' | 'status' | 'delete';
    categoryIds?: string[];
    isActive?: boolean;
  }) =>
    apiSend<{ ok: true; affected: number; blocked?: { id: string; name: string }[] }>(
      '/api/catalogue/items/bulk',
      'POST',
      body,
    ),
  /** Custom order = sort_order by position; an EMPTY list resets to A–Z. */
  setItemOrder: (ids: string[]) => apiSend('/api/catalogue/items/order', 'PUT', { ids }),
  setFavourite: (id: string, favourite: boolean) =>
    apiSend(`/api/catalogue/items/${id}/favourite`, 'PUT', { favourite }),
  createItem: (body: {
    categoryId?: string;
    name: string;
    description?: string;
    icon?: string;
    formLayoutId?: string;
  }) => apiSend<{ id: string }>('/api/catalogue/items', 'POST', body),
  updateItem: (id: string, body: Record<string, unknown>) =>
    apiSend(`/api/catalogue/items/${id}`, 'PATCH', body),
  /** Step 70 — `forEmail` is whoever the requester picker names, so the
   *  prefill re-resolves when an agent changes the requester. */
  itemForm: (id: string, surface?: 'agent' | 'requester', forEmail?: string) => {
    const q = new URLSearchParams();
    if (surface) q.set('surface', surface);
    if (forEmail) q.set('for', forEmail);
    const qs = q.toString();
    return apiFetch<{
      fields: ResolvedField[];
      prefill?: Record<string, string>;
      subjectTemplated?: boolean;
      /** Step 77 — the parts that come with this item. */
      additionalItems?: {
        id: string;
        name: string;
        icon: string | null;
        mandatory: number;
        sortOrder: number;
      }[];
    }>(`/api/catalogue/items/${id}/form${qs ? `?${qs}` : ''}`);
  },
  /* ── Step 77 — bundled "additional items" ───────────────────────── */
  itemLinks: (id: string) =>
    apiFetch<{
      entries: {
        childItemId: string;
        mandatory: number;
        createChildRequest: number;
        sortOrder: number;
        name: string;
        icon: string | null;
        isActive: number;
      }[];
    }>(`/api/catalogue/items/${id}/links`),
  setItemLinks: (
    id: string,
    links: { childItemId: string; mandatory: boolean; createChildRequest: boolean }[],
  ) => apiSend(`/api/catalogue/items/${id}/links`, 'PUT', { links }),

  /* ── Step 69 — visibility ────────────────────────────────────────── */
  requesterGroups: () => apiFetch<{ entries: RequesterGroup[] }>('/api/catalogue/requester-groups'),
  createRequesterGroup: (body: { name: string; rule?: Condition | null; memberIds?: string[] }) =>
    apiSend<{ id: string }>('/api/catalogue/requester-groups', 'POST', body),
  updateRequesterGroup: (
    id: string,
    body: Partial<{ name: string; rule: Condition | null; memberIds: string[] }>,
  ) => apiSend(`/api/catalogue/requester-groups/${id}`, 'PATCH', body),
  deleteRequesterGroup: (id: string) => apiSend(`/api/catalogue/requester-groups/${id}`, 'DELETE'),
  /** Distinct directory values, so a rule picks a real department rather
   *  than a typed one that matches nobody. */
  requesterAttributes: () => apiFetch<Record<string, string[]>>('/api/catalogue/requester-groups/attributes'),
  /** Who a rule admits — runs the enforcing evaluator server-side, so this
   *  cannot say one thing while the catalogue does another. */
  previewRequesterGroup: (rule: Condition) =>
    apiSend<{ total: number; entries: { id: string; name: string | null; email: string }[] }>(
      '/api/catalogue/requester-groups/preview',
      'POST',
      { rule },
    ),
  /** Whole-list replace per axis — see the route's own note on why this is
   *  not folded into the item PATCH. */
  setVisibility: (
    id: string,
    body: {
      agentVisibility: 'all' | 'groups';
      requesterVisibility: 'all' | 'groups';
      agentGroupIds: string[];
      requesterGroupIds: string[];
    },
  ) => apiSend(`/api/catalogue/items/${id}/visibility`, 'PUT', body),
  submitRequest: (
    id: string,
    body: {
      subject: string;
      source?: string;
      requesterEmail: string;
      requesterName?: string;
      /** Step 72 — who it is FOR, when that is not the submitter. */
      onBehalfOf?: { email: string; name?: string };
      /** Step 73 — files already uploaded as `pending`. */
      attachmentIds?: string[];
      /** Step 77 — which OPTIONAL additional items were ticked. */
      additionalItemIds?: string[];
      values: Record<string, unknown>;
    },
  ) => apiSend<{ id: string; number: number }>(`/api/catalogue/items/${id}/request`, 'POST', body),
};
